/**
 * Registers all built-in behaviors with BehaviorRegistry.
 * Also registers Trace-specific FlowGraph blocks.
 *
 * This module works in two ways:
 * 1. Side-effect import: `import './registerAll'` — runs registrations at module eval time
 * 2. Explicit call: `registerBuiltInBehaviors()` — for entry points that need a live export
 *    to prevent tree-shaking in production builds
 *
 * Entry points (traceviewer.ts, exports/behaviors.ts) use the explicit call so the
 * bundler cannot tree-shake the registrations from the dist.
 */

import { registerBehavior as _registerBehavior } from './BehaviorRegistry';
import { Animation } from './animation';
import { AnimationSequence } from './animationsequence';
import { AudioClip } from './audioclip';
import { AudioSource } from './audiosource';
import { AutoUI } from './autoui';
import { BehaviorGraph } from './behaviorgraph/behaviorgraph';
import { Billboard } from './billboard';
import { BoxCollider } from './boxcollider';
import { ContentSlot } from './contentslot';
import { Tether } from './tether';
import { Frame } from './frame';
import { Image } from './image';
import { ImageAnchor } from './imageanchor';
import { Layout } from './layout';
import { LayoutItem } from './layoutitem';
import { MarkerSlot } from './markerslot';
import { Model } from './model';
import { Moveable } from './moveable';
import { Keyframes } from './keyframes';
import { Pages } from './pages';
import { ParticleSystem } from './particlesystem';
import { Properties } from './properties';
import { Provenance } from './provenance';
import { AiSession } from './aisession';
import { RestRelativeBinding } from './restrelativebinding';
import { RecordingInfo } from './recordinginfo';
import { Renderer } from './renderer';
import { Rotatable } from './rotatable';
import { Rotate } from './rotate';
import { SphereCollider } from './spherecollider';
import { Bevel } from './bevel';
import { MorphStretch } from './morphstretch';
import { MeshBackground } from './meshbackground';
import { Synth } from './synth';
import { Text } from './text';
import { Timeline } from './timeline';
import { Video } from './video';
import { ViewAnchor } from './viewanchor';

let _registered = false;

function _doRegister(): number {
    if (_registered) return 0;
    _registered = true;

    // Counted rather than returned as a literal: the hand-maintained number
    // said 29 while 32 behaviors were being registered, and nothing noticed
    // because nothing reads the value for anything but tree-shaking.
    let count = 0;
    const registerBehavior: typeof _registerBehavior = ctor => {
        count++;
        _registerBehavior(ctor);
    };

    registerBehavior(Animation);
    registerBehavior(AnimationSequence);
    registerBehavior(AudioClip);
    registerBehavior(AudioSource);
    registerBehavior(AutoUI);
    registerBehavior(BehaviorGraph);
    registerBehavior(Billboard);
    registerBehavior(BoxCollider);
    registerBehavior(ContentSlot);
    registerBehavior(Tether);
    registerBehavior(Frame);
    registerBehavior(Image);
    registerBehavior(ImageAnchor);
    registerBehavior(Layout);
    registerBehavior(LayoutItem);
    registerBehavior(MarkerSlot);
    registerBehavior(Model);
    registerBehavior(Moveable);
    registerBehavior(Keyframes);
    registerBehavior(Pages);
    registerBehavior(ParticleSystem);
    registerBehavior(Properties);
    registerBehavior(Provenance);
    registerBehavior(AiSession);
    registerBehavior(RestRelativeBinding);
    registerBehavior(RecordingInfo);
    registerBehavior(Renderer);
    registerBehavior(Rotatable);
    registerBehavior(Rotate);
    registerBehavior(SphereCollider);
    registerBehavior(Bevel);
    registerBehavior(MorphStretch);
    registerBehavior(MeshBackground);
    registerBehavior(Synth);
    registerBehavior(Text);
    registerBehavior(Timeline);
    registerBehavior(Video);
    registerBehavior(ViewAnchor);

    return count;
}

// Side-effect: runs on import for dev/test environments
_doRegister();

/**
 * Explicit registration entry point. Safe to call multiple times.
 * Entry points export the return value to prevent tree-shaking.
 * @returns The number of behaviors registered (0 if already called).
 */
export function registerBuiltInBehaviors(): number {
    return _doRegister();
}
